import { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TripStatusPill } from "@/components/StatusPills";
import TripFormDialog from "@/components/TripFormDialog";
import { Search, X, Phone, MapPin, DollarSign, Calendar as CalIcon, User, Download, Filter, Pencil, Trash2, ArrowUpCircle, ArrowDownCircle, Package, Wallet, Headset } from "lucide-react";
import NotesHoverTip from "@/components/NotesHoverTip";
import ReceiptLink from "@/components/ReceiptLink";
import StatusHistoryButton from "@/components/StatusHistoryButton";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import useAutoRefresh from "@/lib/useAutoRefresh";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

// Naples-EDT date helpers — used for the "Hoy" / "Ayer" quick-picks and the
// default filter (History opens showing ONLY today's trips instead of every
// completed/cancelled trip ever).
const NAPLES_TZ = "America/New_York";
const naplesDateISO = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() - offsetDays);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: NAPLES_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(d); // YYYY-MM-DD
};
const TODAY_YMD = () => naplesDateISO(0);
const YESTERDAY_YMD = () => naplesDateISO(1);
// Accounting week runs Friday → Thursday (Naples). Returns days since the
// most recent Friday, computed on the Naples calendar day.
const daysSinceFriday = () => {
  const wd = new Intl.DateTimeFormat("en-US", { timeZone: NAPLES_TZ, weekday: "short" }).format(new Date());
  const idx = ["Fri", "Sat", "Sun", "Mon", "Tue", "Wed", "Thu"].indexOf(wd);
  return idx < 0 ? 0 : idx;
};
const THIS_WEEK = () => ({ start: naplesDateISO(daysSinceFriday()), end: TODAY_YMD() });
const THIS_MONTH = () => ({ start: TODAY_YMD().slice(0, 8) + "01", end: TODAY_YMD() });
const PREV_WEEK = () => {
  const back = daysSinceFriday();
  return { start: naplesDateISO(back + 7), end: naplesDateISO(back + 1) };
};

const emptyFilters = {
  status: "all",       // all | completed | cancelled | both
  phone: "",
  pickup: "",
  dropoff: "",
  price_min: "",
  price_max: "",
  driver_id: "all",
  // Default range: today (Naples). The user opens History and by default only
  // sees "carreras del día de hoy" — no scrolling through months of history.
  start: "",
  end: "",
  has_credit: false,   // audit view: only trips that consumed client credit
  trip_type: "all",    // all | trip | delivery
  payment_method: "all", // all | cash | zelle | credito
  created_by: "all",   // all | auto | <dispatcher user id>
};

const defaultFilters = () => ({ ...emptyFilters, start: TODAY_YMD(), end: TODAY_YMD() });

export default function History() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // `filters` = what the dispatcher is typing (draft). `applied` = what the
  // table actually shows. Searching happens ONLY on the Lupa button / Enter
  // / quick-picks — never while typing.
  const [filters, setFilters] = useState(defaultFilters);
  const [applied, setApplied] = useState(defaultFilters);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [pageInfo, setPageInfo] = useState({ total: 0, pages: 1 });
  const [serverTotals, setServerTotals] = useState({ count: 0, revenue: 0, driver_income: 0 });
  const [drivers, setDrivers] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const upd = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  // Query string shared by the paginated history call and the CSV export.
  const buildParams = useCallback(() => {
      const params = new URLSearchParams();
      const st = applied.status;
      if (st === "completed" || st === "cancelled") params.set("status", st);
      else params.set("status", "completed,cancelled"); // default: both closed states
      if (applied.phone) params.set("phone", applied.phone);
      if (applied.pickup) params.set("pickup", applied.pickup);
      if (applied.dropoff) params.set("dropoff", applied.dropoff);
      if (applied.price_min) params.set("price_min", applied.price_min);
      if (applied.price_max) params.set("price_max", applied.price_max);
      if (applied.driver_id && applied.driver_id !== "all") params.set("driver_id", applied.driver_id);
      // Send date-only (YYYY-MM-DD). Backend interprets it as
      // Naples-EDT midnight → correct Naples-day boundaries. If we sent
      // "T00:00:00" the backend parses it as UTC and the range shifts 4-5h,
      // showing trips from the previous Naples day.
      if (applied.start) params.set("start", applied.start);
      if (applied.end) params.set("end", applied.end);
      if (applied.has_credit) params.set("has_credit", "1");
      if (applied.trip_type && applied.trip_type !== "all") params.set("trip_type", applied.trip_type);
      if (applied.payment_method && applied.payment_method !== "all") params.set("payment_method", applied.payment_method);
      if (applied.created_by && applied.created_by !== "all") params.set("created_by", applied.created_by);
      return params;
  }, [applied]);

  // Server-side pagination: 25 rows per page + exact totals computed by
  // Mongo on the same indexed filter, in ONE round-trip.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = buildParams();
      params.set("page", String(page));
      params.set("page_size", String(PAGE_SIZE));
      const { data } = await api.get(`/trips/history?${params.toString()}`);
      setTrips(data.items);
      setPageInfo({ total: data.total, pages: data.pages });
      setServerTotals(data.totals);
    } catch (e) {
      if (!silent) toast.error(formatApiError(e));
    } finally { if (!silent) setLoading(false); }
  }, [buildParams, page]);

  useEffect(() => {
    api.get("/users/drivers").then(({ data }) => setDrivers(data)).catch(() => {});
    api.get("/users/dispatchers").then(({ data }) => setDispatchers(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // Silent auto-refresh every 15s + when the tab regains focus so edits from
  // another dispatcher window show up without hitting the Filter button.
  useAutoRefresh(() => load({ silent: true }), 15000);

  // Run the search with the current draft (Lupa button / Enter key).
  const runSearch = () => { setPage(1); setApplied({ ...filters }); };
  const onEnter = (e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(); } };
  const isDirty = JSON.stringify(filters) !== JSON.stringify(applied);
  // Quick-picks are explicit clicks → they search immediately.
  const setBoth = (f) => { setPage(1); setFilters(f); setApplied(f); };
  // "Limpiar" = whole current month (Naples), per dispatcher request.
  const clearFilters = () => setBoth({ ...emptyFilters, ...THIS_MONTH() });
  const applyTodayFilter = () => setBoth({ ...emptyFilters, start: TODAY_YMD(), end: TODAY_YMD() });
  const applyYesterdayFilter = () => setBoth({ ...emptyFilters, start: YESTERDAY_YMD(), end: YESTERDAY_YMD() });
  const applyThisWeekFilter = () => setBoth({ ...emptyFilters, ...THIS_WEEK() });
  const applyPrevWeekFilter = () => setBoth({ ...emptyFilters, ...PREV_WEEK() });

  const deleteTrip = async (t) => {
    if (!window.confirm(
      `¿Eliminar el viaje ${t.trip_number || t.id}?\n\nEsta acción es permanente y NO se puede deshacer. Los totales de LLC y las liquidaciones no se ajustan automáticamente.`
    )) return;
    try {
      await api.delete(`/trips/${t.id}`);
      toast.success("Viaje eliminado");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportCSV = async () => {
    if (!serverTotals.count) return;
    let rows = trips;
    try {
      const params = buildParams();
      params.set("limit", "0");
      const { data } = await api.get(`/trips?${params.toString()}`);
      rows = data;
    } catch (e) { toast.error(formatApiError(e)); return; }
    const cols = ["_row_ts", "status", "passenger_name", "passenger_phone", "pickup_address", "dropoff_address", "price", "payment_method", "driver_name", "driver_income", "llc_income", "client_debt_payment", "client_credit_applied"];
    const header = ["Fecha", "Estado", "Pasajero", "Teléfono", "Pickup", "Dropoff", "Precio", "Pago", "Chofer", "Driver Inc", "LLC Inc", "Cobrado Chofer", "Crédito aplicado"];
    const lines = [header.join(",")];
    // Cancelled trips export as $0 across all financial columns.
    const _ZERO = new Set(["price", "driver_income", "llc_income", "client_debt_payment", "client_credit_applied"]);
    rows.forEach((t) => {
      // Use completed_at when frozen so exported "Fecha" matches the
      // History table (day the trip actually closed).
      const rowTs = t.completed_at || t.updated_at || "";
      const enriched = { ...t, _row_ts: rowTs };
      lines.push(
        cols
          .map((c) => {
            const val = enriched.status === "cancelled" && _ZERO.has(c) ? 0 : (enriched[c] ?? "");
            return JSON.stringify(val);
          })
          .join(","),
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `historial-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const totals = serverTotals;

  return (
    <AppLayout>
      <div className="p-6 md:p-8 space-y-6 max-w-7xl mx-auto w-full">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-mono text-primary tracking-widest">HISTORIAL</div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight" style={{ fontFamily: "Manrope" }}>
              Historial de viajes
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Viajes completados y cancelados con filtros avanzados.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant="outline"
                onClick={async () => {
                  if (!window.confirm(
                    "Recomputar los splits ROTOS (driver + LLC ≠ precio) de TODOS los viajes cerrados.\n\n" +
                    "Se usará el driver_pct ACTUAL de cada chofer. No cambia inversión ni recibos.\n\n" +
                    "¿Continuar?"
                  )) return;
                  try {
                    const { data: res } = await api.post("/trips/recompute-splits", { only_broken: true });
                    const skipped = Number(res?.skipped_orphans || 0);
                    if (skipped > 0) {
                      toast.warning(
                        `Reparación: ${res.updated} viajes arreglados. ${skipped} saltado${skipped !== 1 ? "s" : ""} por chofer huérfano — revisa Reports.`,
                        { duration: 8000 }
                      );
                    } else {
                      toast.success(`Reparación lista: ${res.updated} viajes arreglados`);
                    }
                    await load();
                  } catch (err) {
                    toast.error(formatApiError(err));
                  }
                }}
                data-testid="history-repair-broken-splits"
                className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 font-bold"
                title="Recomputa los splits de todos los viajes donde chofer + LLC ≠ precio"
              >
                Reparar splits rotos
              </Button>
            )}
            <Button onClick={exportCSV} disabled={!serverTotals.count} className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold" data-testid="history-export-csv">
              <Download className="w-4 h-4 mr-1" /> Exportar CSV
            </Button>
          </div>
        </div>

        <Card className="bg-card border-border p-4 space-y-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs font-mono text-primary tracking-widest flex items-center gap-1.5">
              <Filter className="w-3 h-3" /> FILTROS
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={applyTodayFilter}
                data-testid="history-quick-today"
                className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/15 text-primary hover:bg-primary/25 border border-primary/30"
              >
                Hoy
              </button>
              <button
                onClick={applyYesterdayFilter}
                data-testid="history-quick-yesterday"
                className="text-xs font-semibold px-2.5 py-1 rounded-full bg-accent/15 text-accent hover:bg-accent/25 border border-accent/30"
              >
                Ayer
              </button>
              <button
                onClick={applyThisWeekFilter}
                data-testid="history-quick-this-week"
                title="Semana contable actual (viernes → hoy)"
                className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/30"
              >
                Esta semana
              </button>
              <button
                onClick={applyPrevWeekFilter}
                data-testid="history-quick-prev-week"
                title="Semana contable anterior (viernes → jueves)"
                className="text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-500/15 text-sky-300 hover:bg-sky-500/25 border border-sky-500/30"
              >
                Semana anterior
              </button>
              <button
                onClick={clearFilters}
                data-testid="history-clear-filters"
                title="Quitar filtros y mostrar todo el mes actual"
                className="text-xs text-muted-foreground hover:text-white flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Limpiar (mes actual)
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Estado</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="bg-input border-border" data-testid="history-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Completados y cancelados</SelectItem>
                  <SelectItem value="completed">Solo completados</SelectItem>
                  <SelectItem value="cancelled">Solo cancelados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" /> Chofer</Label>
              <Select value={filters.driver_id} onValueChange={(v) => setFilters((f) => ({ ...f, driver_id: v }))}>
                <SelectTrigger className="bg-input border-border" data-testid="history-driver"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los choferes</SelectItem>
                  {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><CalIcon className="w-3 h-3" /> Desde</Label>
              <Input type="date" value={filters.start} onChange={upd("start")} data-testid="history-start" onKeyDown={onEnter} className="bg-input border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><CalIcon className="w-3 h-3" /> Hasta</Label>
              <Input type="date" value={filters.end} onChange={upd("end")} data-testid="history-end" onKeyDown={onEnter} className="bg-input border-border" />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" /> Teléfono</Label>
              <Input value={filters.phone} onChange={upd("phone")} placeholder="Buscar por número" data-testid="history-phone" onKeyDown={onEnter} className="bg-input border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3 text-primary" /> Pickup</Label>
              <Input value={filters.pickup} onChange={upd("pickup")} placeholder="Buscar dirección de origen" data-testid="history-pickup" onKeyDown={onEnter} className="bg-input border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3 text-accent" /> Dropoff</Label>
              <Input value={filters.dropoff} onChange={upd("dropoff")} placeholder="Buscar dirección de destino" data-testid="history-dropoff" onKeyDown={onEnter} className="bg-input border-border" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><DollarSign className="w-3 h-3" /> Precio</Label>
              <div className="flex gap-2">
                <Input type="number" step="0.01" value={filters.price_min} onChange={upd("price_min")} placeholder="Mín" data-testid="history-price-min" onKeyDown={onEnter} className="bg-input border-border" />
                <Input type="number" step="0.01" value={filters.price_max} onChange={upd("price_max")} placeholder="Máx" data-testid="history-price-max" onKeyDown={onEnter} className="bg-input border-border" />
              </div>
            </div>
            {/* Audit view: quickly surface trips that consumed passenger credit
                from the LLC ledger. Stored as a plain boolean flag on the
                filter state, sent as ?has_credit=1 to the backend. */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Package className="w-3 h-3" /> Tipo</Label>
              <Select value={filters.trip_type} onValueChange={(v) => setFilters((f) => ({ ...f, trip_type: v }))}>
                <SelectTrigger className="bg-input border-border" data-testid="history-trip-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Viajes y deliverys</SelectItem>
                  <SelectItem value="trip">Solo viajes</SelectItem>
                  <SelectItem value="delivery">Solo deliverys</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Headset className="w-3 h-3" /> Creado por</Label>
              <Select value={filters.created_by} onValueChange={(v) => setFilters((f) => ({ ...f, created_by: v }))}>
                <SelectTrigger className="bg-input border-border" data-testid="history-created-by"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los dispatchers</SelectItem>
                  <SelectItem value="auto">Automático (citas)</SelectItem>
                  {dispatchers.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`history-created-by-${d.id}`}>{d.name}{d.active ? "" : " (inactivo)"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Wallet className="w-3 h-3" /> Método de pago</Label>
              <Select value={filters.payment_method} onValueChange={(v) => setFilters((f) => ({ ...f, payment_method: v }))}>
                <SelectTrigger className="bg-input border-border" data-testid="history-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los métodos</SelectItem>
                  <SelectItem value="cash">Efectivo</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="credito">Crédito</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-end gap-3 pb-1">
              <label
                htmlFor="history-has-credit"
                className="inline-flex items-center gap-2 cursor-pointer select-none group pb-2"
                data-testid="history-has-credit-label"
                title="Muestra únicamente viajes donde se consumió crédito del cliente (columna Crédito > 0)"
              >
                <input
                  id="history-has-credit"
                  data-testid="history-has-credit"
                  type="checkbox"
                  checked={filters.has_credit}
                  onChange={(e) => setFilters((f) => ({ ...f, has_credit: e.target.checked }))}
                  className="h-4 w-4 accent-emerald-500 cursor-pointer"
                />
                <span className={`text-xs font-mono tracking-wide ${filters.has_credit ? "text-emerald-300" : "text-muted-foreground group-hover:text-white"}`}>
                  Solo viajes con crédito aplicado
                </span>
              </label>
              <Button
                onClick={runSearch}
                disabled={loading}
                data-testid="history-search-btn"
                title="Buscar con los filtros actuales (o presiona Enter en cualquier campo)"
                className={`ml-auto h-10 px-5 font-bold transition-all ${isDirty ? "bg-primary text-primary-foreground shadow-[0_0_18px_rgba(251,191,36,0.45)] animate-pulse" : "bg-primary/80 text-primary-foreground hover:bg-primary"}`}
              >
                <Search className="w-4 h-4 mr-2" /> Buscar
                {isDirty && <span className="ml-2 text-[9px] font-mono uppercase tracking-widest opacity-80" data-testid="history-search-dirty">· filtros sin aplicar</span>}
              </Button>
            </div>
          </div>
        </Card>

        {/* Summary tiles */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="p-4 bg-card border-border">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono mb-1">Resultados</div>
            <div className="text-2xl md:text-3xl font-black font-mono text-white" data-testid="history-count">{totals.count}</div>
          </Card>
          <Card className="p-4 bg-card border-border">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono mb-1">Facturado</div>
            <div className="text-2xl md:text-3xl font-black font-mono text-amber-400">{fmt(totals.revenue)}</div>
          </Card>
          <Card className="p-4 bg-card border-border">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono mb-1">Ingreso chofer</div>
            <div className="text-2xl md:text-3xl font-black font-mono text-emerald-400">{fmt(totals.driver_income)}</div>
          </Card>
        </div>

        {/* Table */}
        <Card className="bg-card border-border overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead>Nº</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Pasajero</TableHead>
                  <TableHead>Ruta</TableHead>
                  <TableHead>Chofer</TableHead>
                  <TableHead>Pago</TableHead>
                  <TableHead className="text-right">Inv.</TableHead>
                  <TableHead className="text-right" title="Cobrado extra al cliente para saldar deuda con la LLC">Cobrado</TableHead>
                  <TableHead className="text-right" title="Crédito del cliente aplicado a este viaje (consumió saldo a favor de la LLC)">Crédito</TableHead>
                  <TableHead className="text-right">Precio</TableHead>
                  <TableHead className="text-right w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow className="border-border"><TableCell colSpan={11} className="text-center text-muted-foreground py-8">Cargando…</TableCell></TableRow>
                )}
                {!loading && trips.length === 0 && (
                  <TableRow className="border-border"><TableCell colSpan={11} className="text-center text-muted-foreground py-12">
                    Sin viajes con esos filtros.
                  </TableCell></TableRow>
                )}
                {trips.map((t) => (
                  <TableRow key={t.id} className="border-border" data-testid={`history-row-${t.id}`}>
                    <TableCell className="font-mono text-xs font-bold text-primary whitespace-nowrap" data-testid={`history-trip-number-${t.id}`}>
                      {t.trip_number || "—"}
                    </TableCell>
                    <TableCell className="font-mono text-[10px] whitespace-nowrap text-muted-foreground">
                      {(() => {
                        // Show `completed_at` (frozen at close) when
                        // present so a post-close edit doesn't visually
                        // migrate the trip to the day it was edited.
                        const ts = t.completed_at || t.updated_at;
                        return ts ? new Date(ts).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—";
                      })()}
                    </TableCell>
                    <TableCell><TripStatusPill status={t.status} compact /></TableCell>
                    <TableCell className="max-w-[130px] truncate text-sm" title={t.passenger_name}>
                      {t.passenger_name || "—"}
                    </TableCell>
                    <TableCell
                      className="max-w-[240px] text-xs leading-tight"
                      title={`${t.pickup_address || ""} → ${t.dropoff_address || ""}`}
                    >
                      <div className="truncate">
                        <MapPin className="w-2.5 h-2.5 text-primary inline mr-0.5" />
                        {t.pickup_address || "—"}
                      </div>
                      <div className="truncate text-muted-foreground">
                        <MapPin className="w-2.5 h-2.5 text-accent inline mr-0.5" />
                        {t.dropoff_address || "—"}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[110px] truncate text-sm" title={t.driver_name}>
                      {t.driver_name || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-[10px] uppercase font-mono text-muted-foreground">
                      {(() => {
                        // Contribution of this trip to the driver's daily balance vs LLC:
                        //   contribution = cash_from_this_trip − driver_income − driver_investment
                        // Positive → driver owes LLC · Negative → LLC owes driver.
                        // Zelle and "credito" (fully-credit-paid) never touch driver's cash.
                        // Cash trips with partial credit reduce the cash by the applied amount.
                        const pm = (t.payment_method || "").toLowerCase();
                        const credit = Number(t.client_credit_applied || 0);
                        const cash = pm === "cash" ? Math.max(0, Number(t.price || 0) - credit) : 0;
                        const contrib = cash - Number(t.driver_income || 0) - Number(t.driver_investment || 0);
                        let Icon = null, color = "", title = "";
                        if (contrib > 0.005) {
                          Icon = ArrowUpCircle; color = "text-amber-400";
                          title = `Chofer entrega $${contrib.toFixed(2)} a LLC`;
                        } else if (contrib < -0.005) {
                          Icon = ArrowDownCircle; color = "text-cyan-400";
                          title = `LLC debe $${Math.abs(contrib).toFixed(2)} al chofer`;
                        }
                        return (
                          <span className="inline-flex items-center gap-1">
                            <span className={(t.payment_method || "").toLowerCase() === "credito" ? "text-emerald-300 font-semibold" : ""}>
                              {({ cash: "efectivo", zelle: "Zelle", credito: "Crédito", card: "tarjeta" }[(t.payment_method || "").toLowerCase()]) || t.payment_method}
                            </span>
                            {Icon && (
                              <Icon
                                className={`w-3 h-3 ${color}`}
                                title={title}
                                data-testid={`history-contrib-${t.id}-${contrib > 0 ? "pos" : "neg"}`}
                              />
                            )}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      <div className="flex items-center justify-end gap-1">
                        <span className={Number(t.driver_investment || 0) > 0 && t.status !== "cancelled" ? "text-cyan-400 font-bold" : "text-muted-foreground"}>
                          {fmt(t.status === "cancelled" ? 0 : t.driver_investment)}
                        </span>
                        <ReceiptLink
                          receipt={t.investment_receipt}
                          testId={`history-receipt-${t.id}`}
                          label=""
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      <span className={Number(t.client_debt_payment || 0) > 0 && t.status !== "cancelled" ? "text-amber-300 font-bold" : "text-muted-foreground"}>
                        {fmt(t.status === "cancelled" ? 0 : t.client_debt_payment)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs" data-testid={`history-credit-applied-${t.id}`}>
                      <span
                        className={Number(t.client_credit_applied || 0) > 0 && t.status !== "cancelled" ? "text-emerald-300 font-bold" : "text-muted-foreground"}
                        title={Number(t.client_credit_applied || 0) > 0
                          ? `El cliente aplicó $${Number(t.client_credit_applied).toFixed(2)} de su saldo a favor a este viaje`
                          : "Sin crédito aplicado"}
                      >
                        {fmt(t.status === "cancelled" ? 0 : t.client_credit_applied)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold text-amber-400 whitespace-nowrap">{fmt(t.status === "cancelled" ? 0 : t.price)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <StatusHistoryButton trip={t} size="sm" />
                        <NotesHoverTip trip={t} testId={`history-notes-tip-${t.id}`}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setEditing(t); setEditOpen(true); }}
                            data-testid={`history-edit-${t.id}`}
                            className="border-border h-7 w-7 p-0"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        </NotesHoverTip>
                        {isAdmin && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteTrip(t)}
                            data-testid={`history-delete-${t.id}`}
                            className="border-red-500/40 h-7 w-7 p-0 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                            title="Eliminar viaje (solo admin)"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {pageInfo.pages > 1 && (
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border bg-card/60" data-testid="history-pagination">
              <div className="text-xs font-mono text-muted-foreground" data-testid="history-page-info">
                Mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, pageInfo.total)} de {pageInfo.total} · página {page}/{pageInfo.pages}
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => setPage(1)} disabled={page <= 1 || loading} className="border-border h-8 px-2" data-testid="history-page-first">«</Button>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} className="border-border h-8" data-testid="history-page-prev">Anterior</Button>
                {Array.from({ length: pageInfo.pages }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === pageInfo.pages || Math.abs(n - page) <= 2)
                  .reduce((acc, n, i, arr) => { if (i && n - arr[i - 1] > 1) acc.push("…"); acc.push(n); return acc; }, [])
                  .map((n, i) => n === "…"
                    ? <span key={`gap-${i}`} className="px-1 text-muted-foreground text-xs">…</span>
                    : (
                      <button
                        key={n} type="button" onClick={() => setPage(n)} disabled={loading}
                        data-testid={`history-page-${n}`}
                        className={`h-8 min-w-[32px] px-2 rounded text-xs font-mono font-bold border transition-colors ${n === page ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-white hover:border-primary/60"}`}
                      >
                        {n}
                      </button>
                    ))}
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pageInfo.pages, p + 1))} disabled={page >= pageInfo.pages || loading} className="border-border h-8" data-testid="history-page-next">Siguiente</Button>
                <Button variant="outline" size="sm" onClick={() => setPage(pageInfo.pages)} disabled={page >= pageInfo.pages || loading} className="border-border h-8 px-2" data-testid="history-page-last">»</Button>
              </div>
            </div>
          )}
        </Card>
      </div>
      <TripFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        trip={editing}
        drivers={drivers}
        onSaved={load}
      />
    </AppLayout>
  );
}
